import { useEffect, useMemo, type ReactNode } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useT } from "../i18n";
import { useSitzung } from "../schale/Sitzung";
import { Kopf, Titel } from "../schale/Kopf";
import { FehlerBand, Laedt } from "../bausteine";
import { Abschnitt as Block, Leersatz, Zeilenblatt, Zweizeiler, type Punkt } from "../bausteine/Handyformen";
import { useHandyHeute, useWiedervorlageErledigt, useWiedervorlagen } from "../daten/haken";
import type { HandyTag, HandyZeile, Nachbereitungszeile } from "../daten/typen";
import { useMeinTag } from "../daten/einsatzzeit";
import { Vergessen } from "./handy/MeinEinsatz";
import type { MeinTag } from "../daten/einsatzzeit";
import { abschlusswort, dauer } from "../daten/einsatzlage";
import { useWarteschlange } from "../daten/warteschlange";
import type { Wartender } from "../daten/warteschlange";
import { Aufnahmeaufgaben } from "./handy/Aufnahmeaufgaben";
import { Vorlagezeile } from "./handy/Startzeilen";
import { useJetzt } from "../bausteine/Einsatzuhr";
import { useZiehauffrischen } from "../bausteine/Ziehauffrischen";
import { wannText } from "../bausteine/Vorbereitung";
import "../stile/handy.css";
import "../stile/mappe.css";
import "../stile/telefon/heute.css";
import { tagImBetrieb, tageZwischen as tageZwischenImBetrieb } from "../daten/betriebstag";

/* ══════════════════════════════════════════════════════════════════
   Der Handy-Startschirm: Heute.

   [09-mobile.md](../../../../docs/09-mobile.md): *„Am Telefon steht man
   auf dem Hof, im Regen, mit Handschuhen, und hat eine Hand frei."*

   **Was hier NICHT hinkommt:** das Dispobrett, ein Angebot mit acht
   Positionen, Stammdaten, Abrechnungsläufe. Und keine Preise — der
   Techniker sieht keine Marge und keine offenen Posten
   (20-prozesse.md, D1).

   ── Eine Liste, nach der Uhr — 17.09.2026 ─────────────────────────

   Bis hierher standen drei Abschnitte untereinander: VORBEREITEN, RAUS,
   REIN. Luca: *„Vor allem sieht das scheiße aus mit Vorbereiten, Raus,
   Rein, optisch allein."* Und dieselbe Maschine stand zweimal da — unter
   VORBEREITEN und unter RAUS, ohne dass eine der beiden Zeilen sagte, dass
   die andere noch offen ist.

   **Jetzt ist jeder Einsatz eine Zeile**, und die Aufgabe steht als Marke
   darin — Übergabe, Rücknahme, Vorbereiten, läuft:

   ```
   │ LÄUFT JETZT ①                        │
   │ ▌KR-01 Kran MKG 401        ● 4:12 h  │
   │ ▌Bauer Hochbau · Solarpark Nord      │
   │ ▌[Tag 2 von 5] [vor Ort]             │
   │ MEINE ②                              │
   │ ▌RL-07 Radlader 5 t          07:30   │
   │ ▌Schulz KG · Cloppenburg  [Übergabe] │
   │ FREI ZUM ÜBERNEHMEN ①                │
   │ ▌TL-03 Teleskoplader         10:00   │
   ```

   * **„Läuft jetzt" steht oben** — die Anfahrt oder die Baustelle, die
     gerade zählt, und die Einsätze, die draußen sind und heute nicht
     zurückkommen (`laufend`). Ein Kran von Montag bis Freitag bleibt so
     jeden Tag in der Liste.
   * **Darunter, was mir gehört**, dann **was frei zum Übernehmen ist**, und
     für die Disposition, **was andere haben** — mit ihrem Namen. Eine
     Aufgabe mit eingeplantem Fahrer ist nicht „frei", auch wenn niemand sie
     genommen hat: Sie gehört dem, der fährt.
   * **Innerhalb nach der Uhr.** Liegengebliebenes zuerst.

   **Die Zeile führt in den Einsatz** (`/einsatz/:belegung`); was dort
   als Nächstes dran ist, sagt der Einsatz.

   ── Ein Einsatz, ein Eintrag, ein Stand — 18.09.2026 ──────────────

   Luca: *„Aber macht es nicht Sinn, dass das immer der gleiche Vorgang ist?
   Also ich habe einen Vorgang mit dem Status ‚vorbereiten', vorher kann ich
   gar nicht losfahren. Dann fahre ich los. Und wenn ich nicht losfahren kann,
   gibt es dann halt die Übergabe. So stelle ich sicher, dass ich nicht zwei
   Einträge in der Tagesmappe habe."*

   Die Zeile gab es seit dem 17.09. schon nur einmal — sie trug aber noch
   **zwei Marken**: „Übergabe" und daneben „nicht vorbereitet". Das liest sich
   wie zwei Aufgaben an einem Einsatz. **Jetzt nennt genau eine Marke den
   Stand**, in der Reihenfolge des Wegs:

   ```
   │ ▌RL-07 Radlader 5 t       morgen 07:30 │  ← gestrichelt: nicht heute
   │ ▌Schulz KG · Cloppenburg                │
   │ ▌[Vorbereiten]                          │
   │ ▌KR-01 Kran MKG 401            ● 4:12 h │
   │ ▌Bauer Hochbau · Solarpark Nord         │
   │ ▌[vor Ort] [Tag 2 von 5]                │
   ```

   Die Vorbereitung ist damit kein Posten neben dem Einsatz, sondern sein
   erster Zustand — so steht sie auch in der Lage (`daten/einsatzlage.ts`,
   `zustandAbleiten`: `vorbereiten` vor `uebernommen`).
   ══════════════════════════════════════════════════════════════════ */

type Aufgabe = "vorbereiten" | "uebergabe" | "ruecknahme" | "laufend";

type Eintrag = {
  z: HandyZeile;
  aufgaben: Aufgabe[];
  /** Wonach sortiert wird: Beginn, Ende oder — was läuft — der Anfang der Phase. */
  wann: string;
  laeuft: { art: "anfahrt" | "arbeit"; seit: string } | null;
  tag: { nr: number; gesamt: number } | null;
};

/** Kalendertage zwischen zwei Zeitpunkten, in deutscher Zeit (N8-02). */
function tageZwischen(a: Date, b: Date): number {
  return tageZwischenImBetrieb(tagImBetrieb(a), tagImBetrieb(b));
}

/**
 * Was gerade läuft — vom Server (`GET /handy/einsatz`) und vom Gerät: Wer im
 * Funkloch losgefahren ist, steht trotzdem oben.
 */
function laeuftAn(bedienerBelegung: string | null, tag: MeinTag | undefined, wartende: Wartender[]) {
  if (!bedienerBelegung) return null;
  let stand: Eintrag["laeuft"] = null;
  const e = tag?.heute.find((x) => x.belegungId === bedienerBelegung);
  if (e?.unterwegs) stand = { art: "anfahrt", seit: e.unterwegs.seit };
  if (e?.laeuft) stand = { art: "arbeit", seit: e.laeuft.seit };
  for (const w of wartende) {
    if (w.abgewiesen || w.nutzlast["belegung_id"] !== bedienerBelegung) continue;
    if (w.weg === "/handy/einsatz/losfahren") stand = { art: "anfahrt", seit: w.erfasstAm };
    else if (w.weg === "/handy/einsatz/angekommen") stand = { art: "arbeit", seit: w.erfasstAm };
    else if (w.weg === "/handy/einsatz/ausstempeln") stand = null;
  }
  return stand;
}

/** Aus vier Listen eine: je Einsatz eine Zeile, die Aufgaben als Marken. */
function eintraegeBauen(daten: HandyTag, meinTag: MeinTag | undefined, wartende: Wartender[]): Eintrag[] {
  const je = new Map<string, Eintrag>();
  const dazu = (z: HandyZeile, aufgabe: Aufgabe, wann: string | null) => {
    const da = je.get(z.belegungId);
    if (da) {
      if (!da.aufgaben.includes(aufgabe)) da.aufgaben.push(aufgabe);
      // Die Übergabe gewinnt gegen die Vorbereitung: Ihre Uhrzeit ist die des Tages.
      if (aufgabe !== "vorbereiten") da.z = z;
      return;
    }
    je.set(z.belegungId, { z, aufgaben: [aufgabe], wann: wann ?? "", laeuft: null, tag: null });
  };
  for (const z of daten.laufend ?? []) dazu(z, "laufend", z.bisIso);
  for (const z of daten.rein) dazu(z, "ruecknahme", z.bisIso);
  for (const z of daten.raus) dazu(z, "uebergabe", z.vonIso);
  for (const z of daten.vorbereiten) dazu(z, "vorbereiten", z.vonIso);

  const heute = new Date();
  return [...je.values()].map((e) => {
    const laeuft = laeuftAn(e.z.bedienerBelegungId, meinTag, wartende);
    let tag: Eintrag["tag"] = null;
    if (e.z.vonIso && e.z.bisIso) {
      const von = new Date(e.z.vonIso);
      const gesamt = tageZwischen(von, new Date(e.z.bisIso)) + 1;
      const nr = tageZwischen(von, heute) + 1;
      if (gesamt > 1 && nr >= 1 && nr <= gesamt) tag = { nr, gesamt };
    }
    return { ...e, laeuft, tag, wann: laeuft?.seit ?? e.wann };
  });
}

const reihenfolge = (a: Eintrag, b: Eintrag) =>
  Number(b.z.ueberfaellig) - Number(a.z.ueberfaellig) || a.wann.localeCompare(b.wann);

/** Eine Zeile der Liste — ein Einsatz, seine Aufgaben als Marken. */
function Zeile({ e, abschnitt, jetzt, gehZu }: {
  e: Eintrag;
  abschnitt: "laeuft" | "meine" | "frei" | "andere";
  jetzt: number;
  gehZu: () => void;
}) {
  const t = useT();
  const { z } = e;
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const ort = [z.ort, z.ortschaft].filter(Boolean).join(trenn);
  const hat = (a: Aufgabe) => e.aufgaben.includes(a);
  const rueck = hat("ruecknahme");
  const tage = z.vonIso ? tageZwischen(new Date(), new Date(z.vonIso)) : null;
  /* **Was heute vorzubereiten ist, geht morgen hinaus** — und darf nicht
     aussehen wie eine Maschine, die heute vom Hof fährt (18.09.2026). Die
     Zeile bleibt in der Mappe, aber leise: Die Zeit rechts steht in
     Zweitrang (`.th-wann-spaeter`), und das Wort sagt es dazu („morgen
     07:30"). */
  const spaeter = !e.laeuft && !rueck && !hat("uebergabe") && !hat("laufend") && (tage ?? 0) > 0;

  const rechts = e.laeuft
    ? t("einsatz.heute.laeuftUhr", { dauer: dauer((jetzt - new Date(e.laeuft.seit).getTime()) / 1000) })
    : rueck && z.bisIso ? wannText(t, z.bisIso, tageZwischen(new Date(), new Date(z.bisIso)))
      : hat("laufend") && z.bisIso ? t("einsatz.heute.bis", { tag: wannText(t, z.bisIso, tageZwischen(new Date(), new Date(z.bisIso))) })
        : wannText(t, z.vonIso, tage);

  /* Wer den Einsatz hat — nur, wo es nicht schon der Abschnitt sagt. Ein
     eingeplanter Fahrer ist nicht „frei" (17.09.2026: Die Disposition sah
     bei einer Aufgabe mit Fahrer „frei"). */
  const wer = abschnitt === "andere"
    ? z.genommen?.name ?? (z.bediener.length ? t("einsatz.heute.faehrt", { wer: z.bediener.map((b) => b.name).join(", ") }) : "")
    : "";

  /* ── Eine Marke, nicht zwei — 18.09.2026 ──────────────────────────
     Luca: *„Aber macht es nicht Sinn, dass das immer der gleiche Vorgang
     ist? … So stelle ich sicher, dass ich nicht zwei Einträge in der
     Tagesmappe habe."* Hier standen „Übergabe" und daneben „nicht
     vorbereitet" — zwei Aufgaben an einer Zeile, obwohl es ein Einsatz mit
     einem Stand ist.

     **Genau eine Marke sagt, wo der Einsatz steht** — in der Reihenfolge
     seines Wegs: Vorbereiten → Übergabe → unterwegs → vor Ort → beim Kunden
     → Rücknahme. Was gerade läuft, gewinnt: Wer schon unterwegs ist, ist
     nicht mehr am Vorbereiten; die Fragen holt dann die Übergabe nach
     (`HandyProtokoll.tsx`).

     **Die Warnung bleibt davon unberührt** — „Sollte längst draußen sein"
     ist kein Stand, sondern ein Alarm, und steht davor. Ist die Übergabe
     heute und die Vorbereitung noch offen, trägt die Marke das abgedunkelte
     Gelb: derselbe Stand, nur eilig. */
  const stand: { pfad: string; art?: "gut" | "acht" } | null = e.laeuft
    ? { pfad: e.laeuft.art === "anfahrt" ? "einsatz.heute.unterwegs" : "einsatz.heute.vorOrt", art: "gut" }
    : rueck ? { pfad: abschlusswort(z.bediener.length > 0, "einsatz.heute.ruecknahme") }
      : hat("vorbereiten")
        ? { pfad: "einsatz.heute.vorbereiten", ...(hat("uebergabe") ? { art: "acht" as const } : {}) }
        : hat("uebergabe") ? { pfad: "einsatz.heute.uebergabe" }
          : hat("laufend") ? { pfad: "einsatz.heute.draussen" } : null;

  /* ── Eine Zeile wie jede andere — 19.09.2026 ─────────────────────
     Luca: *„…das muss einheitlich sein von der Struktur, sodass die
     Buttons sauber sind und diese Einträge in der Liste immer gleich hoch
     sind, damit es ein konsistentes Design einfach ist."*

     Bis hierher war ein Einsatz eine Karte aus vier Stockwerken (Kennung,
     Kunde und Ort über zwei Zeilen, dann die Marken) mit farbigem Streifen
     — 130 bis 165 px hoch, je nachdem wie lang der Ort war und wie viele
     Marken dastanden. Darunter die Ortsaufnahme als Zeile von 56 px. Zwei
     Formen auf einem Schirm.

     **Jetzt ist es der `Zweizeiler`** wie überall: oben Kennung und Gerät,
     darunter zuerst der Stand und dann Kunde und Ort, rechts die Zeit. Was
     vorher Streifen und Marke trugen, trägt der Punkt (Ampel aus
     28-designkonzept.md) und das erste Wort der zweiten Zeile — der Alarm
     „Sollte längst draußen sein" steht dort vor dem Stand, in Rot. */
  const warnung = z.ueberfaellig ? t(rueck ? "handy.spaetRein" : "handy.spaetRaus") : null;
  const standwort = stand ? t(stand.pfad) : null;
  const tagwort = e.tag && (e.laeuft || hat("laufend") || rueck) ? t("einsatz.uhr.tagZahl", e.tag) : null;
  const vorn = [warnung, standwort].filter(Boolean).join(trenn);
  const hinten = [tagwort, z.kunde || t("handy.ohneKunde"), ort, wer].filter(Boolean).join(trenn);
  const punkt: Punkt = z.ueberfaellig ? "stopp"
    : stand?.art === "gut" ? "gut"
      : stand?.art === "acht" ? "acht"
        : "ruhig";

  return (
    <Zweizeiler
      beiKlick={gehZu}
      punkt={punkt}
      titel={<><span className="th-kennung">{z.kennung}</span>{z.typ ? ` ${t("allgemein.trennpunkt")} ${z.typ}` : ""}</>}
      unter={(
        <>
          {vorn ? <span className={z.ueberfaellig ? "th-stand th-stand-spaet" : "th-stand"}>{vorn}</span> : null}
          {vorn && hinten ? trenn : ""}
          {hinten}
        </>
      )}
      rechts={<Wann text={rechts} leise={spaeter} />}
    />
  );
}

/**
 * Die Zeit rechts, zweistöckig: oben der Wert, darunter leise, wovon er
 * handelt — „07:00" über „heute", „23.09." über „bis am".
 *
 * **Warum nicht in einer Zeile** (19.09.2026): „bis am 23.09." in Mono ist
 * rund 105 px breit und nahm Gerät und Kunden daneben fast alles — in der
 * Zeile stand dann „KR-01 · Kran MKG …". Gestapelt ist die Spalte halb so
 * breit, und der Wert, nach dem man sucht, steht zuerst — so zeigen es auch
 * Kalender. Getrennt wird am letzten Stück mit einer Ziffer; eine Einheit
 * dahinter („2:13 h") bleibt beim Wert.
 */
function Wann({ text, leise }: { text: string; leise: boolean }) {
  const teile = text.split(" ");
  let ab = teile.length - 1;
  while (ab > 0 && !/\d/.test(teile[ab] ?? "")) ab -= 1;
  const oben = teile.slice(ab).join(" ");
  const unten = teile.slice(0, ab).join(" ");
  return (
    <span className={leise ? "th-wann th-wann-spaeter" : "th-wann"}>
      <b>{oben}</b>
      {unten ? <small>{unten}</small> : null}
    </span>
  );
}

function Abschnitt({ titel, eintraege, abschnitt, jetzt, oeffnen, dazu, dazuZahl = 0 }: {
  titel: string;
  eintraege: Eintrag[];
  abschnitt: "laeuft" | "meine" | "frei" | "andere";
  jetzt: number;
  oeffnen: (e: Eintrag) => void;
  /** Zeilen, die unter den Einsätzen stehen — die Wiedervorlagen (21.09.2026). */
  dazu?: ReactNode;
  dazuZahl?: number;
}) {
  const t = useT();
  if (!eintraege.length && !dazuZahl) return null;
  return (
    <Block
      titel={t(titel)}
      auskunft={t("handyBlock.offen", { zahl: eintraege.length + dazuZahl })}
    >
      <Zeilenblatt>
        {eintraege.map((e) => (
          <Zeile key={e.z.belegungId} e={e} abschnitt={abschnitt} jetzt={jetzt} gehZu={() => oeffnen(e)} />
        ))}
        {dazu}
      </Zeilenblatt>
    </Block>
  );
}

/* ══════════════════════════════════════════════════════════════════
   **Die Ortsaufnahme gehört in den Tag** — 18.09.2026, abends, und
   nachgeschärft am 19.09.2026.

   Luca am 18.09.: *„Überprüf mal auch die Ortsaufnahmen. … Es wäre ja eine
   Tagesmappe zu finden."* Seitdem stand sie hier — aber **nur die
   eigenen**. Luca am 19.09.: *„Wenn wir eine offene Ortsaufnahme haben,
   dann muss die Aufgabe ja schön auf dem Handy auch auftauchen."* Eine
   frisch bestellte Aufnahme gehört niemandem, sie liegt im Pool — und stand
   damit auf keinem Telefon.

   **Jetzt stehen hier meine und die freien**, aus derselben Antwort wie
   die Einsätze (`GET /handy/heute`, `aufnahmen`) statt aus einer zweiten
   Abfrage — und damit auch im Funkloch. Der Abschnitt ist ein Baustein
   (`handy/Aufnahmeaufgaben.tsx`), weil der Start ihn genauso trägt.
   ══════════════════════════════════════════════════════════════════ */

/** Der eine Satz, wenn wirklich nichts ansteht. */
function Nichts() {
  const t = useT();
  return <Leersatz>{t("handyHeute.nichtsSatz")}</Leersatz>;
}

/** Die Tagesmappe des Fahrers: eine Liste nach der Uhr. */
/**
 * **Der Tag selbst** — die Einsätze, nach Abschnitten (21.09.2026 ein
 * Baustein und keine eigene Maske mehr).
 *
 * Luca: *„Manchmal wird ein Modul, und dann, je nach Rolle, verändert sich
 * das Modul … Dann kann ja auch derjenige, der alle Rechte hat, das ja auch
 * gerne alles sehen. Das ist ja auch: Wer hat welche Aufträge heute?"*
 *
 * Er steht seitdem in `masken/HeuteTelefon.tsx` (und am Schreibtisch in
 * `masken/Heute.tsx`) zwischen „Anfangen" und der Wiedervorlage — statt
 * unter einem eigenen Fach, das neben „Start" stand und dieselben Zeilen
 * ein zweites Mal zeigte.
 */
export function Tagesmappe({ ohneAufnahmen }: { ohneAufnahmen?: boolean } = {}) {
  /* Runterziehen aktualisiert diesen Schirm (`bausteine/Ziehauffrischen.tsx`). */
  useZiehauffrischen();
  const gehZu = useNavigate();
  const { ich } = useSitzung();
  const { data, isPending, isError, refetch } = useHandyHeute();
  const meinTag = useMeinTag();
  const wartende = useWarteschlange();
  /* **Die Wiedervorlagen gehören in „Meine Aufgaben" — aber erst am Tag**
     (21.09.2026). Luca: *„Eine Wiedervorlage muss aber ja erst unter meine
     Aufgaben auftauchen, wenn der Tag auch gekommen ist, nicht vorher."*

     `faellig` ist genau das: heute oder überfällig (`daten/typen.ts`). Was
     nächsten Dienstag dran ist, steht heute in keiner Aufgabenliste — es
     wäre eine Zeile, die man jeden Tag liest und jeden Tag wegsieht. Zu
     finden ist es weiter unter „Nachfassen" (`/nachfassen`), wo die Fristen
     mit ihrem Vorlauf stehen. */
  const { data: vorlagenRoh } = useWiedervorlagen();
  const vorlagen = (vorlagenRoh ?? []).filter((v) => v.faellig);
  const erledigen = useWiedervorlageErledigt();

  /* **Die Masken dahinter werden gleich mitgeholt** (0113). Der Router lädt
     sie erst beim Hingehen, und im Funkloch lässt sich nichts nachladen: Wer
     dort auf eine Zeile tippt, bekäme ein leeres Bild statt des Einsatzes. */
  useEffect(() => {
    void import("./handy/Einsatz");
    void import("./HandyProtokoll");
    void import("./HandyVorbereitung");
  }, []);

  const eintraege = useMemo(
    () => (data ? eintraegeBauen(data, meinTag.data, wartende) : []),
    [data, meinTag.data, wartende],
  );
  const jetzt = useJetzt(eintraege.some((e) => e.laeuft) ? 30_000 : null);
  const t = useT();

  if (isPending) return <Laedt zeilen={4} />;
  /* Ohne Netz UND ohne Mappe: Dann steht hier nicht „Fehler", sondern was
     die Lage ist. Eine leere Liste behauptet „heute ist nichts" — und das
     ist etwas anderes als „ich weiß es gerade nicht" (0113). */
  if (isError || !data) {
    return (
      <>
        <p className="netzband" role="status">
          <b>{t("offline.ohneMappeTitel")}</b>
          <span>{t("offline.ohneMappeSatz")}</span>
        </p>
        <FehlerBand pfad="fehler.unbekannt" nochmal={() => void refetch()} />
      </>
    );
  }

  const oeffnen = (e: Eintrag) =>
    void gehZu({ to: "/einsatz/$belegung", params: { belegung: e.z.belegungId } });

  const laeuft = eintraege.filter((e) => e.laeuft || e.aufgaben.includes("laufend")).sort(reihenfolge);
  const rest = eintraege.filter((e) => !laeuft.includes(e));
  const meine = rest.filter((e) => e.z.meins || e.z.genommen?.id === ich?.id).sort(reihenfolge);
  const frei = rest.filter((e) => !meine.includes(e) && !e.z.genommen && !e.z.bediener.length && e.z.darfNehmen)
    .sort(reihenfolge);
  const andere = rest.filter((e) => !meine.includes(e) && !frei.includes(e)).sort(reihenfolge);

  return (
    <>
      <Abschnitt titel="einsatz.heute.laeuftJetzt" eintraege={laeuft} abschnitt="laeuft" jetzt={jetzt} oeffnen={oeffnen} />
      {/* ══ „Meine Aufgaben" statt „Meine" — 21.09.2026 ══
          Luca: *„Bei meinen Aufgaben laufen dann ja auch die Wiedervorlagen
          rein … Die kann dann ja unter ‚Meine' auftauchen, und dann nennt es
          ‚Meine Aufgaben'."*

          Daneben stand bis hierher ein eigener Abschnitt „Wiedervorlage" —
          zwei Listen für dieselbe Frage („was liegt bei mir?"), und die
          zweite auch dann, wenn ihr Eintrag erst nächste Woche dran ist.
          Jetzt stehen die Einsätze oben (sie haben eine Uhrzeit) und die
          Wiedervorlagen darunter (sie haben ein Datum). */}
      <Abschnitt
        titel="aufgaben.meineTitel"
        eintraege={meine}
        abschnitt="meine"
        jetzt={jetzt}
        oeffnen={oeffnen}
        dazuZahl={vorlagen.length}
        dazu={vorlagen.map((v) => (
          <Vorlagezeile
            key={v.id}
            v={v}
            laeuft={erledigen.isPending}
            erledigen={(vid: string, fertig: () => void) => erledigen.mutate(vid, { onSuccess: fertig })}
          />
        ))}
      />
      <Abschnitt titel="einsatz.heute.frei" eintraege={frei} abschnitt="frei" jetzt={jetzt} oeffnen={oeffnen} />
      <Abschnitt titel="einsatz.heute.andere" eintraege={andere} abschnitt="andere" jetzt={jetzt} oeffnen={oeffnen} />
      {/* Der Satz gehört zu den Einsätzen und steht deshalb bei ihnen: „Keine
          Vorbereitung, keine Übergabe, keine Rücknahme" bleibt richtig, auch
          wenn darunter eine Ortsaufnahme liegt — die hat keine Uhrzeit. */}
      {/* **Die Nachbereitung** (P8-02, Entscheidung 06.10.2026): Nachtanken
          steht bei dem, dem es zufällt („Meine"), oder offen bei Hof &
          Technik („Frei"). Ein Tipp führt an den Einsatz, direkt an das Feld
          „Nachgetankt (Liter)". */}
      <Nachbereiten zeilen={data.nachbereiten ?? []} />
      {eintraege.length || (data.nachbereiten ?? []).length ? null : <Nichts />}
      {/* Die Ortsaufnahmen stehen unter den Einsätzen: Sie haben kein Datum
          und keine Uhrzeit, also auch keinen Platz in der Reihenfolge des
          Tages — sie werden mitgenommen, wenn es passt. */}
      {/* **Am Schreibtisch stehen sie in der Karte „Was ist zu tun?"** — mit
          „Übernehmen" daneben (21.09.2026). Sie hier ein zweites Mal zu
          zeigen wäre dieselbe Liste zweimal auf einem Schirm, und genau
          dagegen ist dieses Modul gebaut. */}
      {ohneAufnahmen ? null : <Aufnahmeaufgaben aufgaben={data.aufnahmen} />}
    </>
  );
}

/** Die offenen Nachbereitungen — Nachtanken — in der Tagesmappe (P8-02). */
function Nachbereiten({ zeilen }: { zeilen: Nachbereitungszeile[] }) {
  const t = useT();
  const gehZu = useNavigate();
  if (!zeilen.length) return null;
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  return (
    <Block titel={t("nachbereitung.titel")} auskunft={t("handyBlock.offen", { zahl: zeilen.length })}>
      <Zeilenblatt>
        {zeilen.map((n) => (
          <Zweizeiler
            key={n.protokollId}
            punkt={n.meins || n.frei ? "acht" : "ruhig"}
            titel={t("nachbereitung.nachtankenZeile", { kennung: n.kennung })}
            unter={[
              n.vorgang, n.kunde,
              t(n.meins ? "nachbereitung.meins" : n.frei ? "nachbereitung.frei" : "nachbereitung.bei", { name: n.besitzer ?? "" }),
            ].filter(Boolean).join(trenn)}
            beiKlick={() => void gehZu({ href: n.weg })}
          />
        ))}
      </Zeilenblatt>
    </Block>
  );
}

/**
 * Wer noch keine Rolle hat.
 *
 * **Kein Fehlerband.** Es ist nichts kaputt — es ist nur noch nichts
 * eingerichtet, und ein rotes Band sagte ihm das Gegenteil. Der Satz nennt
 * die Stelle, an der es weitergeht: die Verwaltung (12-offene-fragen.md,
 * Frage 0a: *„ein Fahrer sieht seine Aufgaben erst, wenn die Verwaltung ihm
 * die Rolle gibt"*).
 *
 * **Unten steht dabei keine Leiste** (`schale/handywege.ts`): Sie böte Wege
 * an, die es für ihn noch nicht gibt.
 */
export function OhneRolle() {
  const t = useT();
  return (
    <>
      <Kopf><Titel>{t("handy.titel")}</Titel></Kopf>
      <div className="handy">
        <div className="mappe-ohne">
          <b>{t("handy.ohneRolleKopf")}</b>
          <p>{t("handy.ohneRolleSatz")}</p>
        </div>
      </div>
    </>
  );
}

/**
 * **Der Prüfer** (Befund P5-03, 05.10.2026) — Rolle `pruefer` (0274, B7).
 *
 * Er hat weder den Tag (`tagesmappe`) noch die Arbeit (`start`) und landete
 * deshalb bei `OhneRolle`: „Für dich ist noch keine Rolle hinterlegt" — bei
 * jemandem, dessen Rolle sehr wohl hinterlegt ist. Er bekommt stattdessen,
 * was er sehen kann, und die Wege dorthin: Aufträge, Belege, Archiv und
 * Export, Handbuch. Jeder Weg steht in `schale/rechte.ts` für `pruefen`,
 * `angebote` oder offen; die Einstellungen zeigen ihm nur das Archiv
 * (`einstellungen/seiten.ts`, `einstellungenOffen`).
 */
export const PRUEFERWEGE = [
  { weg: "/auftraege", wort: "prueferStart.auftraege", satz: "prueferStart.auftraegeSatz" },
  { weg: "/belege", wort: "prueferStart.belege", satz: "prueferStart.belegeSatz" },
  { weg: "/einstellungen", seite: "archiv", wort: "prueferStart.archiv", satz: "prueferStart.archivSatz" },
  { weg: "/handbuch", wort: "prueferStart.handbuch", satz: "prueferStart.handbuchSatz" },
] as const;

export function PrueferStart() {
  const t = useT();
  return (
    <>
      <Kopf><Titel>{t("prueferStart.titel")}</Titel></Kopf>
      <div className="handy">
        <div className="mappe-ohne">
          <b>{t("prueferStart.kopf")}</b>
          <p>{t("prueferStart.satz")}</p>
          <nav className="pruefer-wege" aria-label={t("prueferStart.titel")}>
            {PRUEFERWEGE.map((w) => {
              const inhalt = (
                <>
                  <span className="pruefer-wort">{t(w.wort)}</span>
                  <span className="pruefer-satz">{t(w.satz)}</span>
                </>
              );
              return "seite" in w ? (
                <Link key={w.weg} className="pruefer-weg" to="/einstellungen/$seite" params={{ seite: w.seite }}>{inhalt}</Link>
              ) : (
                <Link key={w.weg} className="pruefer-weg" to={w.weg}>{inhalt}</Link>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}

/**
 * **Der vergessene Eintrag von gestern** — im Kopf der Tagesmappe.
 *
 * Er stand bis zum 18.09.2026 auf `/handy/zeiten`. Der Schirm ist an diesem
 * Tag weggefallen (Luca: *„kann weg? wir erfassen die zeiten ja über die
 * vorgänge"*), und mit ihm wäre diese Warnung am Telefon verschwunden — die
 * einzige Stelle, die *ungefragt* sagt, dass gestern nicht ausgestempelt
 * wurde. Der nächste Stempelversuch wird zwar mit `zeit.vortag_offen`
 * abgewiesen, aber dann steht der Fahrer schon am Gerät.
 *
 * Kein eigener Abruf: `useMeinTag()` liest diese Maske ohnehin.
 */
export function Vergessenband() {
  const { data } = useMeinTag();
  const offen = data?.vortagOffen ?? [];
  if (offen.length === 0) return null;
  return <>{offen.map((e) => <Vergessen key={e.id} e={e} />)}</>;
}

/* ══════════════ Die eigene Maske ist am 21.09.2026 weggefallen ══════════════

   Hier stand `Handy()` — der Schirm unter `/heute`, mit Tageskopf, Bändern
   und der Tagesmappe darunter. Luca: *„Wie kann man heute und Start
   zusammenbringen? … Vor allem auf dem Desktop klingt das auch irgendwie
   dumm, weil man da dann irgendwie Tagesmappe und Start hat. Das ist ja
   irgendwie Quatsch."*

   Gemessen war es das auch: `/start` trug einen Abschnitt „Heute" mit vier
   Zeilen aus derselben Liste, beide Schirme zeigten „Ortsaufnahmen", und am
   Schreibtisch standen im Menü „Tagesmappe" und „Start" direkt
   untereinander — die Mappe dort als 520-px-Spalte in einem 1440-px-Fenster.

   **Jetzt ist es ein Modul** (`masken/Heute.tsx`, `masken/HeuteTelefon.tsx`),
   und die Abschnitte darin hängen an Rechten: „Anfangen" und die Arbeit am
   Recht `start`, der Tag am Recht `tagesmappe`. Wer nur eines hat, sieht
   genau das, was er vorher auf seinem Schirm sah; wer beides hat, sieht
   einen Schirm statt zweier.

   Was hier bleibt, sind die Bausteine: `Tagesmappe` (der Tag) und
   `Vergessenband` (der offene Stempel von gestern). */
